import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatCurrency } from '../../lib/utils'
import { InsuranceIndication } from '../../models/insuranceIndication'
import { InsuranceQuote } from '../../models/insuranceQuote'

export function ChatIndicationDetailsContent({
  indication,
  actions,
}: {
  indication: InsuranceIndication | InsuranceQuote
  /** Links and CTAs for this indication — see IndicationDetailActions. */
  actions?: React.ReactNode
}) {
  const truckCount = indication.metadata.truckCount ?? 0
  const showPerTruck = truckCount > 1

  return (
    <div className="w-full">
      <div className="flex justify-between">
        <div>
          <img src={indication.iconUrl} alt={indication.iconAlt} className="h-11 w-14 object-contain" />
          <div className="text-md mt-3 font-semibold">{indication.vendorName}</div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-general-foreground">Estimated total</span>
          <span className="text-xl text-black">{formatCurrency(indication.premium)}</span>
        </div>
      </div>

      <Card className="mt-5 -mr-6 -ml-6 border-none shadow-none">
        <CardContent>
          <div className="text-muted-foreground mb-1 font-semibold">Summary of coverage</div>
          <div className="whitespace-pre-line text-black">{indication.summary}</div>
        </CardContent>
      </Card>

      <div className="mt-5">
        {indication.coverages.map((item) => {
          const showPerTruckForItem = showPerTruck && item.isPricePerTruck && item.premium !== null
          const perTruckPrice = item.premiumPerUnit || (item.premium ?? 0) / truckCount
          return (
            <div key={item.label}>
              <div className="text-general-foreground mb-1 text-lg font-semibold">{item.label}</div>
              <div className="mt-1 mb-2 grid grid-cols-[150px_1fr] gap-2">
                <span className="text-muted-foreground text-sm">Estimated premium</span>
                <span className="text-general-foreground text-sm">
                  {item.premium === null ? 'Included' : formatCurrency(item.premium)}
                  {showPerTruckForItem && (
                    <span className="text-muted-foreground">
                      {' '}
                      ({formatCurrency(perTruckPrice)} per truck)
                    </span>
                  )}
                </span>
              </div>
              {item.limit !== null && (
                <div className="mt-1 mb-2 grid grid-cols-[150px_1fr] gap-2">
                  <span className="text-muted-foreground text-sm">Limit</span>
                  <span className="text-general-foreground text-sm">{formatCurrency(item.limit)}</span>
                </div>
              )}
              {item.deductible !== null && (
                <div className="mt-1 mb-2 grid grid-cols-[150px_1fr] gap-2">
                  <span className="text-muted-foreground text-sm">
                    {item.comprehensiveDeductible != null ? 'Collision deductible' : 'Deductible'}
                  </span>
                  <span className="text-general-foreground text-sm">{formatCurrency(item.deductible)}</span>
                </div>
              )}
              {item.comprehensiveDeductible != null && (
                <div className="mt-1 mb-2 grid grid-cols-[150px_1fr] gap-2">
                  <span className="text-muted-foreground text-sm">Comprehensive deductible</span>
                  <span className="text-general-foreground text-sm">
                    {formatCurrency(item.comprehensiveDeductible)}
                  </span>
                </div>
              )}
              <Separator className="mt-4 mb-4" />
            </div>
          )
        })}
      </div>
      {actions}
    </div>
  )
}
